'use client';

import React from 'react';
import { cn } from '@/lib/utils/utils';

interface JContainerBaseProps {
    id?: string;
    ref?: React.RefObject<HTMLDivElement | null>;
    size?:
        | 'sm0'
        | 'sm1'
        | 'sm'
        | 'sm2'
        | 'sm3'
        | 'md'
        | 'md2'
        | 'lg'
        | 'lg2'
        | 'xl'
        | 'full';
    padding?: 'sm' | 'md' | 'lg' | 'none' | 'blog';
    center?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

type JContainerProps = React.PropsWithChildren<JContainerBaseProps>;

/**
 * JContainer - Join Intelligence 统一容器组件
 *
 * 用于替换项目中重复的 w-full max-w-xxx 模式
 *
 * @param size - 容器最大宽度: sm(800px), md(1360px), lg(1680px), xl(1920px), full(无限制)
 * @param padding - 响应式内边距: sm, md, lg, none, blog(博客专用)
 * @param center - 是否水平居中，默认true
 * @param ref - 可选的DOM引用
 * @param className - 额外的CSS类名
 * @param children - 子元素
 */
export default function JContainer({
    id,
    ref,
    size = 'lg',
    padding = 'md',
    className = '',
    children,
    style,
}: JContainerProps) {
    // 尺寸映射 - 基于项目中的实际使用情况
    const sizeClasses = {
        sm0: 'sm:max-w-[700px]', // Blog内容区、部分业务组件
        sm1: 'sm:max-w-[780px]', // Blog内容区、部分业务组件
        sm: 'sm:max-w-[800px]', // Cookie页面、表单页面
        sm3: 'sm:max-w-[900px]',
        sm2: 'sm:max-w-[1240px]',
        md: 'sm:max-w-[1440px]', // Blog内容区、部分业务组件
        md2: 'sm:max-w-[1360px]',
        lg: 'sm:max-w-[1680px]', // 最常用的主容器尺寸
        lg2: 'sm:max-w-[1600px]',
        xl: 'sm:max-w-[1920px]', // 特大容器
        full: 'sm:max-w-full', // 无宽度限制
    };

    // 内边距映射 - 基于项目中的响应式padding模式
    const paddingClasses = {
        none: '', // 无内边距
        sm: 'p-[16px] sm:p-[28px]', // 小间距
        md: 'p-[20px] sm:p-[40px]', // 标准间距（最常用）
        lg: 'p-[28px] sm:p-[60px]', // 大间距
        blog: 'p-[33px] sm:p-0', // 博客专用间距
    };

    const baseClasses = 'w-full mx-auto max-w-full';

    return (
        <div
            id={id}
            ref={ref}
            className={cn(
                baseClasses,
                sizeClasses[size],
                paddingClasses[padding],
                className,
            )}
            style={style}
        >
            {children}
        </div>
    );
}
